import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import PatientsPage from './pages/PatientsPage';
import CalendarPage from './pages/CalendarPage';
import ServicesPage from './pages/ServicesPage';
import BlackoutDaysPage from './pages/BlackoutDaysPage';
import NoShowPage from './pages/NoShowPage';
import ReviewsPage from './pages/ReviewsPage';
import ReportsPage from './pages/ReportsPage';
import UsersPage from './pages/UsersPage';
import WhatsAppPage from './pages/WhatsAppPage';
import WhatsAppRemindersPage from './pages/WhatsAppRemindersPage';
import CRMPage from './pages/CRMPage';
import CRMSettingsPage from './pages/CRMSettingsPage';
import CRMTemplatesPage from './pages/CRMTemplatesPage';
import CRMQuickRepliesPage from './pages/CRMQuickRepliesPage';
import PatientActionPage from './pages/PatientActionPage';
import PatientConfirmPage from './pages/PatientConfirmPage';
import PatientCancelPage from './pages/PatientCancelPage';
import PatientReschedulePage from './pages/PatientReschedulePage';
import RewardWalletPublicPage from './pages/RewardWalletPublicPage';
import JobCardsPage from './pages/JobCardsPage';
import JobCardReportsPage from './pages/JobCardReportsPage';
import TellerPage from './pages/TellerPage';
import TellerConfigPage from './pages/TellerConfigPage';
import TellerReportsPage from './pages/TellerReportsPage';
import FollowupCallsPage from './pages/FollowupCallsPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import ServiceReportsPage from './pages/ServiceReportsPage';
import EbitdaPage from './pages/EbitdaPage';
import IncentivePage from './pages/IncentivePage';
import PatientPortalLoginPage from './pages/PatientPortalLoginPage';
import PatientPortalDashboardPage from './pages/PatientPortalDashboardPage';
import SettingsPage from './pages/SettingsPage';
import AppUsersPage from './pages/AppUsersPage';
import BackupsPage from './pages/BackupsPage';
import SystemLogsPage from './pages/SystemLogsPage';
import RewardServicesPage from './pages/RewardServicesPage';
import RewardSettingsPage from './pages/RewardSettingsPage';
import RewardReportsPage from './pages/RewardReportsPage';
import PackagesPage from './pages/PackagesPage';
import PharmacySalesPage from './pages/PharmacySalesPage';
import WhatsAppTemplatesPage from './pages/WhatsAppTemplatesPage';
import WhatsAppTemplateGuidePage from './pages/WhatsAppTemplateGuidePage';
import ServicesAuditPage from './pages/ServicesAuditPage';
import FeedbackRatePage from './pages/FeedbackRatePage';
import TaskUploadPage from './pages/TaskUploadPage';
import RegisterPage from './pages/RegisterPage';
import PatientFeedbackPage from './pages/PatientFeedbackPage';
import AppointmentConfirmPage from './pages/AppointmentConfirmPage';
import AccessLogsPage from './pages/AccessLogsPage';
import { Toaster } from './components/ui/sonner';
import useIdleLogout from './hooks/useIdleLogout';
import { AccessModeProvider, useAccessMode } from './context/AccessModeContext';

// Front Desk loses the Dashboard while Eclipse mode is on → land on Calendar instead.
const FrontDeskHome = ({ user, onLogout }) => {
  const { eclipse } = useAccessMode();
  if (user.role === 'FrontDeskExecutive' && eclipse) return <Navigate to="/calendar" replace />;
  return <Dashboard user={user} onLogout={onLogout} />;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('lastActivityAt');
    setUser(null);
  }, []);

  useIdleLogout(!!user, handleLogout, 60);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  // Helper to determine default route based on role
  const getDefaultRoute = () => {
    if (!user) return '/login';
    if (user.role === 'Therapist') return '/job-cards';
    if (user.role === 'Teller') return '/teller';
    if (user.role === 'FeedbackReviewer') return '/patient-feedback';
    return '/';
  };

  return (
    <>
      <AccessModeProvider user={user}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={!user ? <LoginPage onLogin={handleLogin} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/" element={
            user 
              ? (user.role === 'Therapist' ? <Navigate to="/job-cards" /> : user.role === 'Teller' ? <Navigate to="/teller" /> : <FrontDeskHome user={user} onLogout={handleLogout} />)
              : <Navigate to="/login" />
          } />
          <Route path="/calendar" element={user && (user.role === 'Admin' || user.role === 'FrontDeskExecutive') ? <CalendarPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/patients" element={user && user.role !== 'Therapist' && user.role !== 'Teller' ? <PatientsPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/services" element={user && user.role !== 'Therapist' && user.role !== 'Teller' ? <ServicesPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/job-cards" element={user && (user.role === 'Admin' || user.role === 'Therapist' || user.role === 'Teller') ? <JobCardsPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/blackouts" element={user && user.role === 'Admin' ? <BlackoutDaysPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/no-show" element={user && user.role === 'Admin' ? <NoShowPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/review" element={user && user.role === 'Admin' ? <ReviewsPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/reports" element={user && user.role === 'Admin' ? <ReportsPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/job-card-reports" element={user && user.role === 'Admin' ? <JobCardReportsPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/users" element={user && user.role === 'Admin' ? <UsersPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/whatsapp" element={user && user.role === 'Admin' ? <WhatsAppPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/whatsapp-reminders" element={user && user.role === 'Admin' ? <WhatsAppRemindersPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          {/* CRM (Meta WhatsApp) Routes — Admin + Front-Desk Executive */}
          <Route path="/crm" element={user && user.role === 'Admin' ? <CRMPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/crm/replies" element={user && user.role === 'Admin' ? <CRMQuickRepliesPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/crm/templates" element={user && user.role === 'Admin' ? <CRMTemplatesPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/crm/settings" element={user && user.role === 'Admin' ? <CRMSettingsPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          {/* Public Patient Action Page - No Auth Required */}
          <Route path="/patient/action/:appointmentId/:token" element={<PatientActionPage />} />
          <Route path="/patient/confirm/:appointmentId/:token" element={<PatientConfirmPage />} />
          <Route path="/patient/cancel/:appointmentId/:token" element={<PatientCancelPage />} />
          <Route path="/patient/reschedule/:appointmentId/:token" element={<PatientReschedulePage />} />
          {/* Teller Routes */}
          <Route path="/teller" element={user && (user.role === 'Teller' || user.role === 'Admin') ? <TellerPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/teller-config" element={user && user.role === 'Admin' ? <TellerConfigPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/teller-reports" element={user && user.role === 'Admin' ? <TellerReportsPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          {/* Followup Calls */}
          <Route path="/followup-calls" element={user && user.role === 'Admin' ? <FollowupCallsPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          {/* Analytics Dashboard - Admin Only */}
          <Route path="/analytics" element={user && user.role === 'Admin' ? <AnalyticsDashboard user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/service-reports" element={user && user.role === 'Admin' ? <ServiceReportsPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/ebitda" element={user && user.role === 'Admin' ? <EbitdaPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/incentives" element={user && user.role === 'Admin' ? <IncentivePage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          {/* Patient Portal - Public OTP Login */}
          <Route path="/patient-portal" element={<PatientPortalLoginPage />} />
          <Route path="/patient-portal/dashboard" element={<PatientPortalDashboardPage />} />
          {/* Public Rewards Wallet — opened from WhatsApp URL button */}
          <Route path="/rewards/wallet/:token" element={<RewardWalletPublicPage />} />
          {/* Public feedback rating + reward-task upload (WhatsApp URL buttons) */}
          <Route path="/feedback/:score/:token" element={<FeedbackRatePage />} />
          <Route path="/task-upload/:token" element={<TaskUploadPage />} />
          <Route path="/register/:token" element={<RegisterPage />} />
          <Route path="/confirm/:token" element={<AppointmentConfirmPage />} />
          {/* Patient Feedback staff section (Admin + Feedback Reviewer) */}
          <Route path="/patient-feedback" element={user && (user.role === 'Admin' || user.role === 'FeedbackReviewer') ? <PatientFeedbackPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          {/* Admin App Settings */}
          <Route path="/settings" element={user && user.role === 'Admin' ? <SettingsPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/app-users" element={user && user.role === 'Admin' ? <AppUsersPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/backups" element={user && user.role === 'Admin' ? <BackupsPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/system-logs" element={user && user.role === 'Admin' ? <SystemLogsPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/access-logs" element={user && user.role === 'Admin' ? <AccessLogsPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          {/* Eden Reward Points */}
          <Route path="/rewards/services" element={user && user.role === 'Admin' ? <RewardServicesPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/rewards/settings" element={user && user.role === 'Admin' ? <RewardSettingsPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/rewards/reports" element={user && user.role === 'Admin' ? <RewardReportsPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          {/* Packages (service bundles) */}
          <Route path="/packages" element={user && user.role === 'Admin' ? <PackagesPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/pharmacy-sales" element={user && user.role === 'Admin' ? <PharmacySalesPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/whatsapp-templates" element={user && user.role === 'Admin' ? <WhatsAppTemplatesPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/whatsapp-guide" element={user && user.role === 'Admin' ? <WhatsAppTemplateGuidePage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
          <Route path="/admin/services-audit" element={user && user.role === 'Admin' ? <ServicesAuditPage user={user} onLogout={handleLogout} /> : <Navigate to={getDefaultRoute()} />} />
        </Routes>
      </BrowserRouter>
      </AccessModeProvider>
      <Toaster position="top-right" richColors />
    </>
  );
}

export default App;
